import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Search as SearchIcon, X, Disc3, BookOpen } from "lucide-react";
import { http } from "@/lib/api";
import Artwork from "@/components/Artwork";
import { trackSearch } from "@/lib/analytics";

const HINTS = ["Hanuman", "Shiva", "Sanskrit", "Workout", "Meditation", "Chalisa", "Bhajan"];

const useDebounce = (v, ms = 220) => {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
};

const Search = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const dq = useDebounce(q, 220);
  const [res, setRes] = useState({ releases: [], lyrics: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  useEffect(() => {
    if (!dq) {
      setRes({ releases: [], lyrics: [] });
      return;
    }
    let cancelled = false;
    setLoading(true);
    http
      .get("/search", { params: { q: dq } })
      .then((r) => !cancelled && setRes(r.data))
      .catch(() => !cancelled && setRes({ releases: [], lyrics: [] }))
      .finally(() => { if (!cancelled) { setLoading(false); trackSearch(dq); } });
    return () => {
      cancelled = true;
    };
  }, [dq]);

  const empty = useMemo(() => res.releases.length === 0 && res.lyrics.length === 0, [res]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-2xl"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="mx-auto max-w-2xl px-6 pt-28"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="glass-strong rounded-2xl overflow-hidden border border-[#FF9933]/25">
              <div className="flex items-center gap-3 px-5 py-4">
                <SearchIcon size={18} className="text-[#FF9933]" />
                <input
                  autoFocus
                  data-testid="search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search songs, lyrics, chants…"
                  className="flex-1 bg-transparent outline-none text-white placeholder:text-white/40"
                />
                <button
                  onClick={onClose}
                  className="text-white/60 hover:text-white p-1"
                  data-testid="search-close"
                  aria-label="Close search"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="border-t border-white/5 max-h-[60vh] overflow-y-auto">
                {!q && (
                  <div className="p-5">
                    <div className="text-[10px] uppercase tracking-[0.35em] text-white/40 mb-3">Try</div>
                    <div className="flex flex-wrap gap-2">
                      {HINTS.map((h) => (
                        <button
                          key={h}
                          onClick={() => setQ(h)}
                          className="px-3 py-1.5 text-xs rounded-full border border-white/10 text-white/70 hover:border-[#FF9933] hover:text-[#FF9933] transition"
                        >
                          {h}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {q && loading && <div className="px-5 py-6 text-sm text-white/50">Searching…</div>}
                {q && !loading && empty && (
                  <div className="px-5 py-8 text-sm text-white/50">No results for “{q}”.</div>
                )}
                {res.releases.length > 0 && (
                  <div className="px-2 py-2">
                    <div className="px-3 py-2 text-[10px] uppercase tracking-[0.35em] text-[#FF9933]">Releases</div>
                    {res.releases.map((r) => (
                      <Link
                        key={r.id}
                        to={`/releases/${r.id}`}
                        onClick={onClose}
                        className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-white/5"
                      >
                        <div className="w-10 h-10"><Artwork src={r.cover_image} alt="" className="w-full h-full object-cover" rounded="rounded" /></div>
                        <div className="min-w-0">
                          <div className="text-sm text-white truncate">{r.title}</div>
                          <div className="text-xs text-white/50 truncate">{r.genre}</div>
                        </div>
                        <Disc3 className="ml-auto text-white/30" size={16} />
                      </Link>
                    ))}
                  </div>
                )}
                {res.lyrics.length > 0 && (
                  <div className="px-2 py-2 border-t border-white/5">
                    <div className="px-3 py-2 text-[10px] uppercase tracking-[0.35em] text-[#FF9933]">Lyrics</div>
                    {res.lyrics.map((l) => (
                      <Link
                        key={l.id}
                        to={`/lyrics/${l.id}`}
                        onClick={onClose}
                        className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-white/5"
                      >
                        <div className="w-10 h-10 rounded bg-[#FF9933]/10 border border-[#FF9933]/30 flex items-center justify-center text-[#FF9933]">
                          <BookOpen size={16} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm text-white truncate">{l.title}</div>
                          <div className="text-xs text-white/50 truncate">{l.deity}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-white/40 text-center">
              Press ESC to close
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Search;
